/**
 * Le distanze dai bordi dello schermo, in un posto solo.
 *
 * ── L'errore che questo modulo esiste per evitare ──────────────────────────
 *
 * `env(safe-area-inset-*)` **non è un margine da rispettare in aggiunta al
 * proprio**: è lo spazio già occupato da qualcos'altro — la barra di stato in
 * alto, l'indicatore home in basso. Sommarci il proprio respiro lo conta due
 * volte.
 *
 * Su un iPhone l'inset inferiore è 34px. Con `14px + 34px` la barra galleggiava
 * a 48px dal fondo mentre stava a 14px dai lati: era il buco che si vedeva. Con
 * `max(14px, 34px)` la barra scavalca l'indicatore e basta.
 *
 * Vale in entrambe le direzioni. Su desktop e su Android l'inset è 0: chi
 * scrive `env(...)` da solo, senza minimo, ottiene un pulsante appiccicato al
 * bordo. Ogni valore qui sotto è un `max`, mai una somma.
 *
 * Il valore di ripiego di `env()` è esplicito: senza, un browser che non
 * conosce la funzione invalida l'intera dichiarazione invece di ignorarla.
 */

const TOP = "env(safe-area-inset-top, 0px)";
const BOTTOM = "env(safe-area-inset-bottom, 0px)";

/**
 * Il bordo superiore del contenuto.
 *
 * In una scheda del browser l'inset è 0 e serve comunque respiro; in PWA
 * installata l'inset è la barra di stato, e il contenuto ci deve stare sotto
 * con un filo d'aria.
 */
export const INSET_TOP = `max(var(--space-5), calc(${TOP} + var(--space-2)))`;

/**
 * Quanto la barra galleggiante sta sollevata dal fondo.
 *
 * Senza indicatore home vale il distacco di progetto; con l'indicatore, quanto
 * basta a scavalcarlo.
 */
export const BAR_OFFSET = `max(var(--bottom-bar-inset), ${BOTTOM})`;

/**
 * Quanto contenuto lasciare libero in fondo a una schermata **con** la barra.
 *
 * Derivato da `BAR_OFFSET` e non riscritto: erano due formule indipendenti, e
 * avevano già preso strade diverse — una contava il distacco due volte, l'altra
 * una. L'ultima riga di una lista finiva a ottantasei pixel dalla barra.
 */
export const CONTENT_BOTTOM_WITH_BAR = `calc(var(--bottom-bar-height) + ${BAR_OFFSET} + var(--space-4))`;

/** Lo stesso, su una schermata senza barra. */
export const CONTENT_BOTTOM = `max(var(--space-6), calc(${BOTTOM} + var(--space-3)))`;
